// Storage helpers — every bucket is private and every object path starts
// with {org_id}/, so all reads go through signed URLs and all writes go
// through orgPath(). (Storage policies enforce the prefix; this just keeps
// client code from ever building a path by hand.)

import { getOrgContext } from './org.js';

// {org_id}/segment/segment... — strips empty segments defensively.
export function orgPath(...segments) {
  const org = getOrgContext();
  if (!org) throw new Error('orgPath: org context not loaded');
  return [org.id, ...segments].filter(Boolean).join('/');
}

const _urlCache = new Map(); // `${bucket}:${path}` → { url, expiresAt }

export async function signedUrl(sb, bucket, path, ttlSeconds = 3600) {
  const key = `${bucket}:${path}`;
  const hit = _urlCache.get(key);
  if (hit && hit.expiresAt > Date.now() + 30_000) return hit.url;

  const { data, error } = await sb.storage.from(bucket).createSignedUrl(path, ttlSeconds);
  if (error) throw error;
  _urlCache.set(key, { url: data.signedUrl, expiresAt: Date.now() + ttlSeconds * 1000 });
  return data.signedUrl;
}

const EXT_BY_MIME = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/svg+xml': 'svg' };

// Uploads a tenant asset (e.g. the org logo) to {org_id}/{baseName}.{ext},
// overwriting any previous file at that exact path (upsert) — a re-upload in
// the same format replaces cleanly; switching format (e.g. png -> svg)
// leaves the old file behind as a harmless orphan rather than risking a
// delete-then-upload race. Returns the storage path (not a URL — pass it to
// signedUrl() or store it in organizations.logo_path).
export async function uploadOrgAsset(sb, bucket, file, baseName) {
  const ext = EXT_BY_MIME[file.type];
  if (!ext) throw new Error(`Unsupported file type: ${file.type || 'unknown'}`);
  const path = orgPath(`${baseName}.${ext}`);
  const { error } = await sb.storage.from(bucket).upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  _urlCache.delete(`${bucket}:${path}`);
  return path;
}
